import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import styles from './ContainerWithScroll.module.scss';
import cx from 'classnames';


const ContainerWithScroll = forwardRef(({ as = 'div', className, children }, ref) => {

    const allowedTags = ["div", "ul"];
    const [showShadow, setShowShadow] = useState({ top: false, bottom: false });
    const [containerLimits, setContainerLimits] = useState({ top: 0, bottom: 0 });

    const containerRef = React.useRef(null);
    const isListWithScroll = useMemo(() => containerRef?.current?.scrollHeight > containerRef?.current?.offsetHeight, [containerRef?.current?.scrollHeight, containerRef?.current?.offsetHeight]);

    const handleOnListScroll = e => {
        setContainerLimits({ top: e.currentTarget.scrollTop, bottom: e.currentTarget.scrollTop + e.currentTarget.offsetHeight });
        setShowShadow({
            top: e.currentTarget.scrollTop > 0,
            bottom: e.currentTarget.scrollHeight - e.currentTarget.scrollTop - 2 > e.currentTarget.offsetHeight,
        });
    };

    useImperativeHandle(ref, () => ({
        scrollTo: ({ top = 0, left = 0, behavior = 'smooth' }) => {
            if (containerRef?.current) {
                containerRef.current.scrollTo({
                    top,
                    left,
                    behavior,
                });
            }
        },
        getBoundingClientRect: () => containerRef?.current?.getBoundingClientRect(),
        offsetHeight: containerRef?.current?.offsetHeight,
        scrollHeight: containerRef?.current?.scrollHeight,
        scrollTop: containerRef?.current?.scrollTop,
        querySelector: selector => containerRef?.current?.querySelector(selector),
    }));

    return React.createElement(
        allowedTags.find(tag => tag == as) ?? 'div', // Default to 'div' but allow 'ul' or other elements
        {
            style: { '--top': `${containerLimits.top}px`, '--bottom': `${containerLimits.bottom}px` },
            ref: containerRef,
            className: cx(
                styles.container,
                className,
                {
                    [styles.showTopShadow]: isListWithScroll && showShadow.top,
                    [styles.showBottomShadow]: isListWithScroll && showShadow.bottom
                }
            ),
            onScroll: handleOnListScroll
        },
        React.Children.map(children, child =>
            React.isValidElement(child) ? React.cloneElement(child) : child
        )
    );
});

export default ContainerWithScroll;